import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import ProgressBar from 'core/components/progress/ProgressBar'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import { topMiddle } from 'core/elements/menu/defaults'

interface Stats {
  current?: number
  max?: number
}

interface TooltipProps {
  message?: string
  [key: string]: any
}

interface ResourceUsageIndicatorProps {
  stats?: Stats
  units?: string
  precision?: number
  width?: number
  tooltipProps?: TooltipProps
  valueOff?: boolean
  label?: string
  valueConverter?: (value: number) => number
}

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: theme.typography.caption1.fontSize,
  },
  percent: {
    fontWeight: 600,
  },
  usage: {
    whiteSpace: 'nowrap',
  },
  progress: {
    marginTop: theme.spacing(0.5),
  },
}))

const defaultTooltipProps: TooltipProps = {
  ...topMiddle,
  origin: 'left top',
}

const ResourceUsageIndicator: React.FC<ResourceUsageIndicatorProps> = ({
  valueOff = false,
  label,
  valueConverter,
  stats = {},
  units = 'GHz',
  precision = 1,
  width = 140,
  tooltipProps = defaultTooltipProps,
}) => {
  const classes = useStyles()
  const { current = 0, max = 0 } = stats
  const percent = max > 0 ? (current / max) * 100 : 0

  const [curStr, maxStr] = useMemo(
    () => [
      valueConverter ? valueConverter(current).toFixed(precision) : current.toFixed(precision),
      valueConverter ? valueConverter(max).toFixed(precision) : max.toFixed(precision),
    ],
    [current, max, precision, valueConverter],
  )

  return (
    <Tooltip
      {...tooltipProps}
      message={`${curStr} ${units} of ${maxStr} ${units} ${label} (${Math.round(percent)}%)`}
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <span className={classes.percent}>{Math.round(percent)}%</span>
          <Text variant="body2" className={classes.usage}>
            - {`${curStr}/${maxStr} ${units} ${label}`}
          </Text>
        </div>
        <div className={classes.progress}>
          <ProgressBar
            width={width}
            variant="health"
            percent={percent > 100 ? 100 : percent}
            showPercent={false}
            height={4}
          />
        </div>
      </div>
    </Tooltip>
  )
}

export default ResourceUsageIndicator
